import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, Calendar, Shield, Camera, Save, Eye, EyeOff, Bell, Globe, Palette, Settings, BarChart3, Activity, Clock, LogIn, Users, DoorOpen, Trophy, Star, TrendingUp, Award } from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { PasswordChangeRequest } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

const ProfilePage: React.FC = () => {
  const { user: currentUser, refreshUser } = useAuth();
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('profile');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState({
    totalLogins: 0,
    savedVisitors: 0,
    doorEntries: 0,
    lastPasswordChange: null,
    recentActivities: []
  });
  const [checklistPoints, setChecklistPoints] = useState<{
    total_points: number;
    today_points: number;
    week_points: number;
    month_points: number;
    rank: number;
    total_admins: number;
  } | null>(null);
  
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    department: '',
    location_id: 0,
    address: '',
    birth_date: '',
    emergency_contact: '',
    emergency_phone: ''
  });

  // Load data on component mount
  React.useEffect(() => {
    loadData();
    loadUserStats();
    if (currentUser?.role === 'admin' || currentUser?.role === 'superadmin') {
      loadChecklistPoints();
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const locationsResponse = await apiService.getLocations();
      setLocations(Array.isArray(locationsResponse) ? locationsResponse : locationsResponse?.data || []);
      
      if (currentUser) {
        setProfileData({
          name: currentUser.name || '',
          email: currentUser.email || '',
          phone: currentUser.phone || '',
          title: currentUser.title || '',
          department: currentUser.department || '',
          location_id: currentUser.location_id || 0,
          address: currentUser.address || '',
          birth_date: currentUser.birth_date ? currentUser.birth_date.split('T')[0] : '',
          emergency_contact: currentUser.emergency_contact || '',
          emergency_phone: currentUser.emergency_phone || ''
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('profile.errLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadUserStats = async () => {
    try {
      // Simulated user stats - replace with actual API calls
      setUserStats({
        totalLogins: Math.floor(Math.random() * 500) + 100,
        savedVisitors: Math.floor(Math.random() * 50) + 10,
        doorEntries: Math.floor(Math.random() * 200) + 50,
        lastPasswordChange: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000),
        recentActivities: [
          { action: 'Profil güncellendi', date: new Date(Date.now() - 2 * 60 * 60 * 1000) },
          { action: 'Sistem girişi', date: new Date(Date.now() - 5 * 60 * 60 * 1000) },
          { action: 'Parola değiştirildi', date: new Date(Date.now() - 24 * 60 * 60 * 1000) }
        ]
      });
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const loadChecklistPoints = async () => {
    try {
      const data = await apiService.getMyChecklistPoints();
      setChecklistPoints(data);
    } catch (error) {
      console.error('Error loading checklist points:', error);
    }
  };

  const [passwordData, setPasswordData] = useState<PasswordChangeRequest>({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [settingsData, setSettingsData] = useState({
    email_notifications: true,
    language: 'tr',
    timezone: 'Europe/Istanbul',
    theme: 'light',
    sidebar_collapsed: false,
    items_per_page: 25
  });

  // Default to profile tab
  React.useEffect(() => {
    setActiveTab('profile');
  }, []);

  const isAdminRole = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  const tabs = [
    { id: 'profile', label: t('profile.tabProfile'), icon: User },
    { id: 'security', label: t('profile.tabSecurity'), icon: Shield },
    { id: 'notifications', label: t('profile.tabNotifications'), icon: Bell },
    { id: 'preferences', label: t('profile.tabPreferences'), icon: Settings },
    ...(isAdminRole ? [{ id: 'performance', label: t('profile.tabPerformance'), icon: BarChart3 }] : [])
  ];



  const handleProfileSave = async () => {
    if (!currentUser) return;

    try {
      await apiService.updateProfile(profileData);
      await refreshUser();
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('profile.successProfile'));
    } catch (error: any) {
      console.error('Error saving profile:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('profile.errProfileSave'));
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('profile.errPasswordMismatch'));
      return;
    }
    if (passwordData.new_password.length < 8) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('profile.errPasswordLength'));
      return;
    }
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Parola değiştirilirken hata oluştu');
      }
      
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('profile.successPassword'));
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error: any) {
      console.error('Error changing password:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('profile.errPasswordChange'));
    }
  };

  const handleSettingsSave = () => {
    // Simulate API call
    setTimeout(() => {
    }, 500);
    // Here would be the API call to update settings
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const result = await apiService.uploadAvatar(file);
        await refreshUser();
        
        // Activity log ekle
        try {
          await apiService.logActivity({
            action: 'profile_avatar_update',
            description: `${currentUser?.name} profil fotoğrafını güncelledi`,
            category: 'profile'
          });
        } catch (logError) {
          console.error('Activity log error:', logError);
        }
        
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('profile.successAvatar'));
      } catch (error: any) {
        console.error('Avatar upload error details:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('profile.errAvatar'));
      }
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPasswordData({...passwordData, new_password: password, confirm_password: password});
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('profile.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('profile.subtitle')}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:w-64">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="space-y-2">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors min-h-[44px] ${
                      activeTab === tab.id
                        ? 'bg-indigo-100 text-primary'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700'
                    }`}
                  >
                    <IconComponent className="w-4 h-4 mr-3" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">


            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="p-6">
                <div className="flex items-center mb-6">
                  <div className="relative">
                    <img
                      className="w-20 h-20 rounded-full"
                      src={currentUser?.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                      alt={currentUser?.name || 'User'}
                    />
                    <label className="absolute bottom-0 right-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-dark transition-colors">
                      <Camera className="w-3 h-3 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <div className="ml-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{currentUser?.name || (language === 'en' ? 'User' : 'Kullanıcı')}</h2>
                    <p className="text-gray-600 dark:text-gray-400">{currentUser?.title || (language === 'en' ? 'Title' : 'Unvan')}</p>
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 capitalize">
                      {currentUser?.role || 'user'}
                    </span>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldName')}
                      </label>
                      <input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldEmail')}
                      </label>
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldPhone')}
                      </label>
                      <input
                        type="text"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldTitle')}
                      </label>
                      <input
                        type="text"
                        value={profileData.title}
                        onChange={(e) => setProfileData({...profileData, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldDepartment')}
                      </label>
                      <input
                        type="text"
                        value={profileData.department}
                        onChange={(e) => setProfileData({...profileData, department: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldLocation')}
                      </label>
                      <select
                        value={profileData.location_id}
                        onChange={(e) => setProfileData({...profileData, location_id: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value={0}>{t('profile.selectLocation')}</option>
                        {locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldBirthDate')}
                      </label>
                      <input
                        type="date"
                        value={profileData.birth_date}
                        onChange={(e) => setProfileData({...profileData, birth_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldEmergencyContact')}
                      </label>
                      <input
                        type="text"
                        value={profileData.emergency_contact}
                        onChange={(e) => setProfileData({...profileData, emergency_contact: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldEmergencyPhone')}
                      </label>
                      <input
                        type="text"
                        value={profileData.emergency_phone}
                        onChange={(e) => setProfileData({...profileData, emergency_phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('profile.fieldAddress')}
                    </label>
                    <textarea
                      value={profileData.address}
                      onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleProfileSave}
                      className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {t('profile.save')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">{t('profile.passwordChange')}</h3>
                
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('profile.fieldCurrentPassword')}
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-400"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('profile.fieldNewPassword')}
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                        className="w-full px-3 py-2 pr-20 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center">
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="px-2 text-gray-400 hover:text-gray-600 dark:text-gray-400"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={generatePassword}
                          className="px-2 text-indigo-600 hover:text-indigo-800 text-xs"
                        >
                          {t('profile.generatePassword')}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('profile.fieldConfirmPassword')}
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-400"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{t('profile.passwordRequirements')}</h4>
                    <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      <li>• {t('profile.pwdReq1')}</li>
                      <li>• {t('profile.pwdReq2')}</li>
                      <li>• {t('profile.pwdReq3')}</li>
                      <li>• {t('profile.pwdReq4')}</li>
                      <li>• {t('profile.pwdReq5')}</li>
                    </ul>
                  </div>

                  <button
                    onClick={handlePasswordChange}
                    className="w-full flex items-center justify-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    {t('profile.changePassword')}
                  </button>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">{t('profile.notifTitle')}</h3>
                
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('profile.emailNotif')}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('profile.emailNotifDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settingsData.email_notifications}
                        onChange={(e) => setSettingsData({...settingsData, email_notifications: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-gray-800 after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>



                  <div className="flex justify-end">
                    <button
                      onClick={handleSettingsSave}
                      className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {t('profile.save')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'performance' && isAdminRole && (
              <div className="p-6">
                <div className="flex items-center mb-6">
                  <BarChart3 className="w-5 h-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('profile.performanceTitle')}</h3>
                </div>

                {checklistPoints ? (
                  <div className="space-y-6">
                    {/* Rank Banner */}
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-indigo-100 text-sm mb-1">{t('profile.rankLabel')}</p>
                          <div className="flex items-baseline gap-2">
                            <span className="text-4xl font-bold">{checklistPoints.rank}.</span>
                            <span className="text-indigo-200 text-sm">/ {checklistPoints.total_admins} {language === 'en' ? 'admins' : 'admin arasında'}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          {checklistPoints.rank === 1 && <Trophy className="w-12 h-12 text-yellow-300" />}
                          {checklistPoints.rank === 2 && <Award className="w-12 h-12 text-gray-200" />}
                          {checklistPoints.rank === 3 && <Award className="w-12 h-12 text-amber-400" />}
                          {checklistPoints.rank > 3 && <Star className="w-12 h-12 text-indigo-300" />}
                        </div>
                      </div>
                    </div>

                    {/* Points Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-indigo-50 rounded-xl p-4 text-center border border-indigo-100">
                        <p className="text-xs text-indigo-600 font-medium mb-1">{t('profile.today')}</p>
                        <p className="text-3xl font-bold text-indigo-700">{checklistPoints.today_points}</p>
                        <p className="text-xs text-indigo-500 mt-1">{t('profile.points')}</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
                        <p className="text-xs text-purple-600 font-medium mb-1">{t('profile.thisWeek')}</p>
                        <p className="text-3xl font-bold text-purple-700">{checklistPoints.week_points}</p>
                        <p className="text-xs text-purple-500 mt-1">{t('profile.points')}</p>
                      </div>
                      <div className="bg-primary/10 rounded-xl p-4 text-center border border-primary/20">
                        <p className="text-xs text-primary font-medium mb-1">{t('profile.thisMonth')}</p>
                        <p className="text-3xl font-bold text-primary-dark">{checklistPoints.month_points}</p>
                        <p className="text-xs text-primary mt-1">{t('profile.points')}</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
                        <p className="text-xs text-green-600 font-medium mb-1">{t('profile.total')}</p>
                        <p className="text-3xl font-bold text-green-700">{checklistPoints.total_points}</p>
                        <p className="text-xs text-green-500 mt-1">{t('profile.points')}</p>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-start gap-3">
                        <TrendingUp className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('profile.pointSystem')}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('profile.pointSystemDesc')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">{t('profile.loadingPoints')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">{t('profile.prefsTitle')}</h3>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldLanguage')}
                      </label>
                      <select
                        value={settingsData.language}
                        onChange={(e) => setSettingsData({...settingsData, language: e.target.value as 'tr' | 'en'})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="tr">Türkçe</option>
                        <option value="en">English</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldTimezone')}
                      </label>
                      <select
                        value={settingsData.timezone}
                        onChange={(e) => setSettingsData({...settingsData, timezone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="Europe/Istanbul">İstanbul (UTC+3)</option>
                        <option value="Europe/London">Londra (UTC+0)</option>
                        <option value="America/New_York">New York (UTC-5)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldTheme')}
                      </label>
                      <select
                        value={settingsData.theme}
                        onChange={(e) => setSettingsData({...settingsData, theme: e.target.value as 'light' | 'dark' | 'auto'})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="light">{t('profile.themeLight')}</option>
                        <option value="dark">{t('profile.themeDark')}</option>
                        <option value="auto">{t('profile.themeAuto')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('profile.fieldItemsPerPage')}
                      </label>
                      <select
                        value={settingsData.items_per_page}
                        onChange={(e) => setSettingsData({...settingsData, items_per_page: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('profile.sidebarAutoHide')}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('profile.sidebarAutoHideDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settingsData.sidebar_collapsed}
                        onChange={(e) => setSettingsData({...settingsData, sidebar_collapsed: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-gray-800 after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleSettingsSave}
                      className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {t('profile.save')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;